import { Injectable, isDevMode } from '@angular/core';
import { AngularFireDatabase } from "@angular/fire/compat/database";
import { environment } from "../../environments/environment";
import { Employee } from "../interfaces/Employee";
import { TaskEmployeeModel } from "../modules/task/models/taskemployee.model";


import { map, shareReplay } from "rxjs/operators";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { filter, forkJoin, lastValueFrom } from "rxjs";
import { UserData } from "../auth.service";
import { UserRole } from "../components/rename/add-user-role/add-user-role.component";
import { FirebaseErrors } from "../FirebaseErrors";
import { firstValueFrom } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Injectable({
  providedIn: 'root'
})

export class EmployeeService {
  userData: UserData
  env = isDevMode() ? environment.testMode : environment.productionMode
  UserRoles$
    : Observable<UserRole[]>
  mPermissions: UserRole[] = []
  private permissionsUpdated = new BehaviorSubject<boolean>(false);
  permissionsUpdated$ = this.permissionsUpdated.asObservable();

  constructor(private mDatabase: AngularFireDatabase,
    private functions: Functions,) {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) {
      console.warn('No user data found in localStorage');
      this.userData = {} as UserData;
      this.UserRoles$ = new Observable(observer => observer.complete());
      return;
    }

    this.userData = JSON.parse(userDataString) as UserData;

    // `userData` is the Firebase record fetched from `users/{authUid}` and can
    // contain its own stale/incorrect `uid` field (e.g. left over from account
    // recreation). The auth UID stored separately at login is authoritative —
    // prefer it so permissions are always read from the correct RTDB path.
    const authUid = localStorage.getItem('uid') || this.userData?.uid;

    if (!authUid || !this.env?.users) {
      console.warn('Invalid user data or environment configuration', { uid: authUid, users: this.env?.users });
      this.UserRoles$ = new Observable(observer => observer.complete());
      return;
    }

    this.UserRoles$ = this.mDatabase.list<UserRole>(`${this.env.users.trim()}/${authUid.trim()}/permissions`).valueChanges().pipe(shareReplay(1))
    this.UserRoles$.subscribe(res => {
      this.mPermissions = res
    })

  }


  triggerPermissionReload() {
    this.permissionsUpdated.next(true);
  }

  async createUser(email: string, password: string) {
    try {
      const createUserFn = httpsCallable(this.functions, 'creatUserFunctionEmail');
      // @ts-ignore
      const result = await createUserFn({ email, password });
      return result.data;
    } catch (error: any) {
      console.error('Error in createUser:', error);

      if (error.code?.startsWith('auth/')) {
        throw new Error(FirebaseErrors.Parse(error.code));
      }

      // Handle non-auth errors
      switch (error.code) {
        case 'unauthenticated':
          throw new Error('You must be logged in to create users.');
        case 'invalid-argument':
          throw new Error(error.details?.message || 'Invalid input provided.');
        case 'internal':
          throw new Error(error.details?.message || 'Internal server error.');
        default:
          throw new Error(error.message || 'An unexpected error occurred.');
      }
    }
  }

  addEmployee(Employeedata: any, uid: string) {
    return this.mDatabase.object(this.env.users + `/${uid}`).set(Employeedata)
    // return   this.mDatabase.object(`testingEmails/${uid}`).set(Employeedata)
  }

  deleteEmployeeByUid(uid: string) {
    return this.mDatabase.object(this.env.users + `/${uid}`).remove();
  }

  async disableAuthUser(uid: string) {
    const disableFn = httpsCallable(this.functions, 'disableUser');
    return await disableFn({ uid, disabled: true });
  }

  getEmployeeByDriverId(driverId: string) {
    return this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('driverId').equalTo(driverId))
      .snapshotChanges()
      .pipe(
        map(changes => changes.map(c => ({ ...(c.payload.val() as Employee), uid: c.payload.key as string })))
      );
  }

  getEmployee() {
    return this.mDatabase.list<Employee>(this.env.employee).snapshotChanges()
  }

  getUsers() {
    return this.mDatabase.list<Employee>('users').snapshotChanges()
  }
  updateUser(id: string, data: Partial<Employee>) {
    return this.mDatabase.object(`users/${id}`).update(data);
  }


  getTestUserData() {
    return this.mDatabase.list<Employee>('TestUsers').snapshotChanges()
  }
  async getPermissions() {
    return lastValueFrom(this.UserRoles$)
  }

  getCounsellers() {
    // Fetches users and filters for active Counsellor or Sr Counsellor roles
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(employees =>
          employees
            .filter(employee => (employee.role === 'Sr Counsellor' || employee?.role === 'Lead Counsellor' || employee.role === 'Counsellor'))
            .filter(employee => employee.jobtitle !== 'IT')
            .filter(employee => employee.disabled === false) // Filter for non-disabled users
            .sort((a, b) => {
              const nameA = (a.userName || '').toLowerCase();
              const nameB = (b.userName || '').toLowerCase();
              return nameA.localeCompare(nameB);
            })
        ),
        shareReplay(1)
      );
  }
  getCounsellersAndKavita() {
    // Fetches users and filters for active Counsellor or Sr Counsellor roles + Kavita Shetty
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(employees =>
          employees
            .filter(employee =>
            (employee.role === 'Sr Counsellor' ||
              employee?.role === 'Lead Counsellor' ||
              employee.role === 'Counsellor' ||
              employee.userName === 'Kavita Shetty')
            )
            .filter(employee => employee.jobtitle !== 'IT')
            .filter(employee => employee.disabled === false) // Filter for non-disabled users
            .sort((a, b) => {
              const nameA = (a.userName || '').toLowerCase();
              const nameB = (b.userName || '').toLowerCase();
              return nameA.localeCompare(nameB);
            })
        ),
        shareReplay(1)
      );
  }


  getTeachers() {
    const teachers$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Teacher')).valueChanges().pipe(
      map(employees => employees.filter(employee => employee.disabled !== true))
    )
    const srteacher$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Sr Teacher')).valueChanges().pipe(
      map(employees => employees.filter(employee => employee.disabled !== true))
    )

    const ldteacher$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Lead Teacher')).valueChanges().pipe(
      map(employees => employees.filter(employee => employee.disabled !== true))
    )

    const allTeachers$ = combineLatest([teachers$, ldteacher$, srteacher$])
      .pipe(
        map(([teachers, srTeachers, leadTeachers]) => [...teachers, ...srTeachers, ...leadTeachers]),
        map(teachers => teachers
          .filter(teacher => teacher.jobtitle !== 'IT')
          .sort((a, b) => {
            const nameA = (a.userName || '').toLowerCase();
            const nameB = (b.userName || '').toLowerCase();
            return nameA.localeCompare(nameB);
          })),
        shareReplay(1)
      );
    return allTeachers$;
  }

  getManagementAndCounsellors() {
    // Fetches users and filters for management roles
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(employees =>
          employees
            .filter(employee =>

              employee.department === 'Management' ||
              employee.role === 'Counsellor' ||
              employee.role === 'Sr Counsellor' ||
              employee.userName === 'Kavita Shetty' ||
              employee.userName === 'Dwani Gujral' ||
              employee.role === 'Lead Counsellor'
              && employee.disabled !== true
            )
            .filter(employee => employee.disabled !== true)
            .sort((a, b) => {
              const nameA = (a.userName || '').toLowerCase();
              const nameB = (b.userName || '').toLowerCase();
              return nameA.localeCompare(nameB);
            })
        ),
        shareReplay(1)
      );
  }

  getCounsellersFromUsers() {
    return this.mDatabase.list<Employee>('users', ref => ref.orderByChild('role').equalTo('Psychologist')).valueChanges()
  }

  getEmployeeList() {
    const employeeRef = this.mDatabase.list<Employee>(this.env.employee)
    return employeeRef.valueChanges()
  }

  getTaskEmployee() {
    const employeeRef = this.mDatabase.list<TaskEmployeeModel>(this.env.users)
    return employeeRef.valueChanges()
  }

  /**
   * Get all users with email and userName
   */
  getAllUsers(): Observable<Employee[]> {
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(users => users.filter(user => user.email && user.userName)),
        shareReplay(1)
      );
  }

  /** Emails of the active 'Centre Coordinator' user(s) assigned to a given centre — used to route approval emails. */
  getCentreCoordinatorEmails(centreCode: string): Observable<string[]> {
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(users => users
          .filter(user => user.role === 'Centre Coordinator' && user.centreCode === centreCode && user.disabled !== true && !!user.email)
          .map(user => user.email)
        )
      );
  }

  /**
   * Get username by email
   * @param email User's email address
   */
  getUserNameByEmail(email: string): Observable<string | undefined> {
    return this.mDatabase.list<Employee>(this.env.users,
      ref => ref.orderByChild('email').equalTo(email).limitToFirst(1))
      .valueChanges()
      .pipe(
        map(users => users[0]?.userName)
      );
  }
  getUserByUserName(userName: string): Observable<Employee | undefined> {
    return this.mDatabase.list<Employee>(
      this.env.users,
      ref => ref.orderByChild('userName').equalTo(userName).limitToFirst(1)
    )
      .valueChanges()
      .pipe(
        map((users: Employee[]) => users.length > 0 ? users[0] : undefined)
      );
  }


  // Add this property to cache users
  private userCache = new Map<string, string>();

  /**
   * Initialize user cache for faster lookups
   */

  initUserCache(): void {
    this.getAllUsers().subscribe(users => {
      users.forEach(user => {
        if (user.email && user.userName) {
          this.userCache.set(user.email, user.userName);
        }
      });
    });
  }

  /**
   * Get username from cache by email
   * @param email User's email address
   */
  getCachedUserName(email: string): string | undefined {
    return this.userCache.get(email);
  }

  getAllCounsellors() {
    // Fetches users and filters for active Counsellor or Sr Counsellor roles
    return this.mDatabase.list<Employee>(this.env.users)
      .valueChanges()
      .pipe(
        map(employees =>
          employees
            .filter(employee => (employee.role === 'Sr Counsellor' || employee?.role === 'Lead Counsellor' || employee.role === 'Counsellor'))
            .filter(employee => employee.jobtitle !== 'IT')

            .sort((a, b) => {
              const nameA = (a.userName || '').toLowerCase();
              const nameB = (b.userName || '').toLowerCase();
              return nameA.localeCompare(nameB);
            })
        ),
        shareReplay(1)
      );
  }

  /**
   * Get all teachers (including disabled) — used for "All Teachers" toggle in MIS Education
   */
  getAllTeachers() {
    const teachers$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Teacher')).valueChanges();
    const srteacher$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Sr Teacher')).valueChanges();
    const ldteacher$ = this.mDatabase.list<Employee>(this.env.users, ref => ref.orderByChild('role').equalTo('Lead Teacher')).valueChanges();

    return combineLatest([teachers$, ldteacher$, srteacher$])
      .pipe(
        map(([teachers, srTeachers, leadTeachers]) => [...teachers, ...srTeachers, ...leadTeachers]),
        map(teachers => teachers
          .filter(teacher => teacher.jobtitle !== 'IT')
          .sort((a, b) => {
            const nameA = (a.userName || '').toLowerCase();
            const nameB = (b.userName || '').toLowerCase();
            return nameA.localeCompare(nameB);
          })),
        shareReplay(1)
      );
  }
}
